import type { DashboardUnassignedAssignmentItem, DashboardUpcomingWorkdayScheduleItem, WorkerDayAvailabilityItem, WorkerDayLeaveItem } from '@/types'

import {
  buildAttentionList,
  createChecker,
  dayStatusOf,
  findAssignmentProblems,
  formatMinuteRange,
  resolveJobIssue,
  type AttentionItem,
  type Checker,
  type JobIssue,
  type ScheduleWorker,
  type WorkerDayStatus,
} from './eligibility'
import { buildScheduleJobRows, filterRowsByLocationCategory, isActiveEntry, isUnassignedRow, type ScheduleJobRow } from './job-rows'
import { buildWorkerNameLookup, formatHourMinute, parseHourMinuteToMinutes, resolveEntryDay } from './schedule-data'
import { ANY_CATEGORY, type WorkerCategoryIndex } from './worker-categories'

const MINUTES_PER_DAY = 24 * 60

const idOf = (value: number | string | null | undefined) => (value == null ? '' : String(value).trim())

/**
 * Per worker, per day: leave (approved leave request) wins; then the day's availability rows.
 * Rows with times are the shift; only "not available" rows make a day off; no row = unset.
 */
export function buildWorkerDays(availabilityItems: WorkerDayAvailabilityItem[], leaveItems: WorkerDayLeaveItem[]) {
  const acc = new Map<string, { ranges: Array<[number, number]>; available: boolean; unavailable: boolean }>()
  availabilityItems.forEach((item) => {
    const workerId = idOf(item.worker_id)
    const day = String(item.work_date || '').trim()
    if (!workerId || !day) return
    const key = `${workerId}|${day}`
    const entry = acc.get(key) ?? { ranges: [], available: false, unavailable: false }
    acc.set(key, entry)
    if (item.is_available === false) {
      entry.unavailable = true
      return
    }
    entry.available = true
    const from = parseHourMinuteToMinutes(formatHourMinute(String(item.from_time || '')))
    const to = parseHourMinuteToMinutes(formatHourMinute(String(item.to_time || '')))
    if (from == null || to == null) return
    // A shift past midnight counts until the end of the day.
    entry.ranges.push([from, to > from ? to : MINUTES_PER_DAY])
  })

  const days = new Map<string, Record<string, WorkerDayStatus>>()
  const set = (workerId: string, day: string, status: WorkerDayStatus) => {
    const record = days.get(workerId) ?? {}
    record[day] = status
    days.set(workerId, record)
  }

  acc.forEach((entry, key) => {
    const [workerId, day] = key.split('|')
    if (entry.ranges.length > 0) {
      const ranges = [...entry.ranges].sort((left, right) => left[0] - right[0])
      set(workerId, day, { kind: 'shift', ranges, label: ranges.map(([from, to]) => formatMinuteRange(from, to)).join(', ') })
    } else if (entry.unavailable && !entry.available) {
      set(workerId, day, { kind: 'off' })
    }
  })

  leaveItems.forEach((item) => {
    const workerId = idOf(item.worker_id)
    const day = String(item.work_date || '').trim()
    if (!workerId || !day || item.is_on_leave !== true) return
    set(workerId, day, { kind: 'leave', leaveType: String(item.leave_type || '').trim() || null })
  })

  return days
}

/** Category names a worker can do: skill category names plus the names of their category ids. */
function resolveCategories(index: WorkerCategoryIndex | null, categoryNameById: ReadonlyMap<string, string>, workerId: string) {
  const info = index?.get(workerId)
  if (!info) return { categoryNames: [], anyCategory: false, categoriesKnown: false }
  const names = new Set(info.categoryNames)
  info.categories.forEach((id) => {
    const name = categoryNameById.get(id)
    if (name) names.add(name)
  })
  return {
    categoryNames: Array.from(names).sort((left, right) => left.localeCompare(right)),
    anyCategory: info.categories.includes(ANY_CATEGORY),
    categoriesKnown: true,
  }
}

export interface ScheduleBoardInput {
  days: string[]
  schedules: DashboardUpcomingWorkdayScheduleItem[]
  unassignedAssignments: DashboardUnassignedAssignmentItem[]
  availabilityItems: WorkerDayAvailabilityItem[]
  leaveItems: WorkerDayLeaveItem[]
  /** Null while loading or when the workers read failed: the category rule is skipped. */
  categoryIndex: WorkerCategoryIndex | null
  categoryNameById: ReadonlyMap<string, string>
  locationFilter: string
  categoryFilter: string
}

export interface ScheduleBoard {
  days: string[]
  /** Workers with a job, availability or leave in the window (same roster as before), by name. */
  workers: ScheduleWorker[]
  workerById: Map<string, ScheduleWorker>
  /** Every job-day in the window (clashes are checked against all of them). */
  allJobs: ScheduleJobRow[]
  /** Jobs shown after the location / category filters. */
  jobs: ScheduleJobRow[]
  jobByKey: Map<string, ScheduleJobRow>
  check: Checker
  /** Shown jobs that need a worker or have a problem, by day then time. */
  attention: AttentionItem[]
  issueByJobKey: Map<string, JobIssue>
}

export function buildScheduleBoard(input: ScheduleBoardInput): ScheduleBoard {
  const daySet = new Set(input.days)
  const workerDays = buildWorkerDays(input.availabilityItems, input.leaveItems)
  const names = buildWorkerNameLookup(input.schedules, input.availabilityItems, input.leaveItems)

  const rosterIds = new Set<string>()
  input.schedules.forEach((entry) => {
    const workerId = idOf(entry.worker_id)
    if (workerId && isActiveEntry(entry) && daySet.has(resolveEntryDay(entry))) rosterIds.add(workerId)
  })
  ;[...input.availabilityItems, ...input.leaveItems].forEach((item) => {
    const workerId = idOf(item.worker_id)
    if (workerId && daySet.has(String(item.work_date || '').trim())) rosterIds.add(workerId)
  })

  const workers = Array.from(rosterIds)
    .map<ScheduleWorker>((id) => ({
      id,
      name: names.get(id) || `Worker #${id}`,
      ...resolveCategories(input.categoryIndex, input.categoryNameById, id),
      days: workerDays.get(id) ?? {},
    }))
    .sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id))
  const workerById = new Map(workers.map((worker) => [worker.id, worker]))

  const allJobs = input.days.flatMap((day) => buildScheduleJobRows(input.schedules, input.unassignedAssignments, day))
  const jobs = filterRowsByLocationCategory(allJobs, input.locationFilter, input.categoryFilter)
  const check = createChecker(allJobs)
  const attention = buildAttentionList(jobs, workerById, check)

  return {
    days: input.days,
    workers,
    workerById,
    allJobs,
    jobs,
    jobByKey: new Map(jobs.map((job) => [job.key, job])),
    check,
    attention,
    issueByJobKey: new Map(attention.map((item) => [item.job.key, item.issue])),
  }
}

export interface ScheduleStats {
  jobs: number
  needWorker: number
  problems: number
  onShift: number
  totalWorkers: number
}

/** Stat cards: counts over the shown jobs; "on shift" for `day`. */
export function computeScheduleStats(board: ScheduleBoard, day: string): ScheduleStats {
  return {
    jobs: board.jobs.length,
    needWorker: board.jobs.filter(isUnassignedRow).length,
    problems: board.jobs.filter((job) => findAssignmentProblems(job, board.workerById, board.check).length > 0).length,
    onShift: board.workers.filter((worker) => dayStatusOf(worker, day).kind === 'shift').length,
    totalWorkers: board.workers.length,
  }
}

/** Issue of one job (null = fine). Same rule as the attention list, for jobs outside the filters too. */
export const issueOf = (board: ScheduleBoard, job: ScheduleJobRow) => board.issueByJobKey.get(job.key) ?? resolveJobIssue(job, board.workerById, board.check)
